/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-no-duplicate-props */
import TagIcon from "@assets/images/svg/fuda_icon.svg?react";
import PlusIcon from "@assets/images/svg/plus_icon.svg?react";
import { TagsType } from "@components/Common/HomeworkTemplate/Modals/types";
import { FormSectionWrap } from "@components/UI/atoms/Forms/FormComponents";
import FormSectionTitle from "@components/UI/atoms/Forms/SectionTitle";
import { useAuth } from "@contexts/Auth";
import useEnqueueToast from "@lib/hooks/useEnqueueToast";
import { safeString } from "@lib/string-utils";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box, Button, TextField, Theme, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { pullAt } from "lodash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

type AddTagsFieldProps = {
  handleGetTagList: () => TagsType[];
  handleGetTagToRemoveIds: () => string[];
  handleSetTagsValue: (newTags: TagsType[]) => void;
  handleSetTagToRemoveIds: (newTagToRemove: string[]) => void;
  tagsList: TagsType[];
  showRequiredTag: boolean;
  required: boolean;
};

type HandleRemoveTagParams = {
  tag: TagsType;
  index: number;
};

const useStyles = (theme: Theme) => ({
  tagContainer: () => ({
    display: "flex",
    flexWrap: "wrap",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 2),
    gap: theme.spacing(1),
  }),
  tagsWrapper: () => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 2),
    height: 36,
    whiteSpace: "nowrap",
    color: theme.palette.tertiary.mainDark,
    fontWeight: 600,
    backgroundColor: theme.palette.search.lightGreenBackground,
  }),
  tagsWrapperIsDisable: () => ({
    color: theme.palette.customColor.commonGrey,
    backgroundColor: theme.palette.quaternary.opacityGrey,
  }),
  clearButton: () => ({
    cursor: "pointer",
    color: theme.palette.tertiary.mainDark,
    fontSize: 18,
    fontWeight: 600,
  }),
  clearButtonDisabled: () => ({
    cursor: "not-allowed",
    color: theme.palette.quaternary.grey,
    fontSize: 18,
    fontWeight: 600,
  }),
  addButton: {
    color: "rgba(71, 81, 73, 0.3)",
  },
  tagInput: {
    width: 125,
    height: 36,
  },
});

const StyledTagIcon = styled(TagIcon)<{ isDisabled: boolean }>(
  ({ theme, isDisabled }) => ({
    "& > path": {
      fill: isDisabled
        ? theme.palette.customColor.commonGrey
        : theme.palette.tertiary.mainDark,
    },
  }),
);

export const AddTagsField: React.FC<AddTagsFieldProps> = ({
  handleGetTagList,
  handleGetTagToRemoveIds,
  handleSetTagsValue,
  handleSetTagToRemoveIds,
  tagsList,
  showRequiredTag,
  required,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const {
    isCurrentRoles: { isStudent },
  } = useAuth();
  const {
    actions: { showWarning },
  } = useEnqueueToast();
  // the bellow storage is set in: components/Common/ClassNav/PlanClass/Content/ContentCard.tsx
  const classNavTag = localStorage.getItem("classNavTagInfos");
  const [isTagEditing, setIsTagEditing] = useState<boolean>(false);
  const [tagInputValue, setTagInputValue] = useState<TagsType | null>(null);
  const { t } = useTranslation("user_list");

  const handleAddTags = () => {
    if (tagInputValue?.name) {
      const tagsListCopy = handleGetTagList();
      if (tagsListCopy.some((tag) => tag.name === tagInputValue.name)) {
        showWarning(`${tagInputValue.name}はすでに存在します。`, "uniq");
        setIsTagEditing(false);
        setTagInputValue(null);
        return;
      }
      const newTags = [...tagsListCopy, tagInputValue];
      handleSetTagsValue(newTags);
      setTagInputValue(null);
    }
    setIsTagEditing(false);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddTags();
    }

    if (e.key === "Escape") {
      handleAddTags();
    }
  };

  const handleTagInputBlur = () => {
    handleAddTags();
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInputValue({
      dbId: null,
      name: e.target.value,
    });
  };

  const handleRemoveTag = ({ index, tag }: HandleRemoveTagParams) => {
    const tagsListCopy = handleGetTagList();
    const newTagList = [...tagsListCopy];
    if (tag?.dbId) {
      const tagToRemoveCopy = handleGetTagToRemoveIds();
      const newTagToRemove = [...tagToRemoveCopy, tag.dbId];
      handleSetTagToRemoveIds(newTagToRemove);
    }
    pullAt(newTagList, index);
    handleSetTagsValue(newTagList);
  };

  return (
    <FormSectionWrap>
      <FormSectionTitle
        title={t("tags_info")}
        showRequiredTag={showRequiredTag}
        required={required}
      />
      <Box sx={classes.tagContainer}>
        {tagsList.map((tag, index) => {
          const isDisabled =
            (classNavTag && classNavTag === tag.name) ||
            (isStudent && tag?.kind && tag.kind === "system");
          return (
            <Box
              key={`${tag.name}-${index}`}
              data-cy="tag-wrapper"
              sx={
                isDisabled
                  ? [classes.tagsWrapper, classes.tagsWrapperIsDisable]
                  : classes.tagsWrapper
              }
            >
              <StyledTagIcon isDisabled={Boolean(isDisabled)} />
              {tag.name}
              <CancelIcon
                sx={
                  isDisabled ? classes.clearButtonDisabled : classes.clearButton
                }
                onClick={() =>
                  isDisabled ? null : handleRemoveTag({ tag, index })
                }
                data-cy="tag-remove-button"
              />
            </Box>
          );
        })}
        {isTagEditing ? (
          <TextField
            variant="outlined"
            size="small"
            autoFocus
            inputProps={{ "data-cy": "tag-input" }}
            InputProps={{ sx: classes.tagInput }}
            value={safeString(tagInputValue?.name)}
            onChange={handleTagInputChange}
            onKeyDown={handleTagInputKeyDown}
            onBlur={handleTagInputBlur}
          />
        ) : (
          <Button
            color="inherit"
            variant="outlined"
            sx={classes.addButton}
            startIcon={<PlusIcon />}
            onClick={() => setIsTagEditing(true)}
            data-cy="tag-add-button"
          >
            {t("addTag")}
          </Button>
        )}
      </Box>
    </FormSectionWrap>
  );
};
